// services/PinCodeService.js - ReactJS (web)

import api from '../api/api';

// ============================================================
// LOCAL STORAGE KEY
// ============================================================

const USER_DATA_KEY = 'user_data';

// ============================================================
// PIN CODE SERVICE
// ============================================================

class PinCodeService {

  // ==========================================================
  // PIN GENERATION & CREATION
  // ==========================================================

  /**
   * Generate a 6-digit PIN
   * GET /api/pin/generate
   */
  async generatePin() {
    try {
      const response =
        await api.get('/pin/generate');

      return response.data;

    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create and store PIN for user
   * POST /api/pin/create
   */
  async createPin(
    expiryMinutes = 15
  ) {
    try {
      const response =
        await api.post(
          '/pin/create',
          {
            expiryMinutes
          }
        );

      return response.data;

    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==========================================================
  // PIN SENDING
  // ==========================================================

  /**
   * Send PIN via email and SMS
   * POST /api/pin/send
   */
  async sendPin() {
    try {
      const response =
        await api.post('/pin/send');

      return response.data;

    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Mark PIN as sent
   * POST /api/pin/mark-sent
   */
  async markPinSent(
    method
  ) {
    try {
      const response =
        await api.post(
          '/pin/mark-sent',
          {
            method
          }
        );

      return response.data;

    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==========================================================
  // PIN VERIFICATION
  // ==========================================================

  /**
   * Verify PIN
   * POST /api/pin/verify
   */
  async verifyPin(
    pin
  ) {
    try {
      const response =
        await api.post(
          '/pin/verify',
          {
            pin
          }
        );

      return response.data;

    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Verify PIN and return user data
   *
   * POST /api/pin/verify-and-get-user
   *
   * IMPORTANT:
   * After successful verification this method also updates
   * localStorage.user_data with the latest verification state.
   */
  async verifyPinAndGetUser(
    pin
  ) {
    try {
      const response =
        await api.post(
          '/pin/verify-and-get-user',
          {
            pin
          }
        );

      const responseData =
        response.data;

      console.log(
        '[PinCodeService] ========================================'
      );

      console.log(
        '[PinCodeService] 🔐 PIN verification response:',
        responseData
      );

      // ======================================================
      // ONLY UPDATE LOCAL STORAGE AFTER SUCCESS
      // ======================================================

      if (
        responseData?.success === true
      ) {

        // ----------------------------------------------------
        // Try all expected response structures.
        // ----------------------------------------------------

        const backendUser =
          responseData?.data?.user ||
          responseData?.data?.data?.user ||
          responseData?.user ||
          null;

        console.log(
          '[PinCodeService] 👤 Backend user:',
          backendUser
        );

        // ----------------------------------------------------
        // Read the currently stored user.
        // ----------------------------------------------------

        let storedUser = {};

        try {
          const storedUserString =
            localStorage.getItem(
              USER_DATA_KEY
            );

          if (
            storedUserString
          ) {
            storedUser =
              JSON.parse(
                storedUserString
              );
          }

        } catch (storageReadError) {
          console.error(
            '[PinCodeService] ❌ Could not read existing user_data:',
            storageReadError
          );
        }

        // ----------------------------------------------------
        // Build updated user.
        //
        // Backend data wins over old localStorage data.
        // ----------------------------------------------------

        const updatedUser = {
          ...storedUser,
          ...(backendUser || {})
        };

        // ----------------------------------------------------
        // IMPORTANT:
        //
        // If backend returned the verification fields,
        // use those exact values.
        //
        // If the endpoint only says success and doesn't return
        // the fields, use the appropriate verified values.
        // ----------------------------------------------------

        if (
          backendUser?.emailVerified !==
          undefined
        ) {
          updatedUser.emailVerified =
            backendUser.emailVerified === true;
        }

        if (
          backendUser?.phoneVerified !==
          undefined
        ) {
          updatedUser.phoneVerified =
            backendUser.phoneVerified === true;
        }

        // ----------------------------------------------------
        // If backend did not return either field but the PIN
        // verification itself represents successful verification,
        // preserve the values returned by the PIN status slice
        // if they exist.
        //
        // We do NOT blindly turn both true unless the backend
        // response indicates it.
        // ----------------------------------------------------

        if (
          backendUser?.emailVerified ===
            undefined &&
          responseData?.data?.emailVerified !==
            undefined
        ) {
          updatedUser.emailVerified =
            responseData.data.emailVerified ===
            true;
        }

        if (
          backendUser?.phoneVerified ===
            undefined &&
          responseData?.data?.phoneVerified !==
            undefined
        ) {
          updatedUser.phoneVerified =
            responseData.data.phoneVerified ===
            true;
        }

        // ----------------------------------------------------
        // Update localStorage.
        // ----------------------------------------------------

        localStorage.setItem(
          USER_DATA_KEY,
          JSON.stringify(
            updatedUser
          )
        );

        // ----------------------------------------------------
        // Verify what was actually saved.
        // ----------------------------------------------------

        const savedUserString =
          localStorage.getItem(
            USER_DATA_KEY
          );

        let savedUser = null;

        try {
          savedUser =
            savedUserString
              ? JSON.parse(
                  savedUserString
                )
              : null;

        } catch (parseError) {
          console.error(
            '[PinCodeService] ❌ Failed to parse saved user:',
            parseError
          );
        }

        console.log(
          '[PinCodeService] 💾 localStorage updated'
        );

        console.log(
          '[PinCodeService] 📧 emailVerified:',
          savedUser?.emailVerified
        );

        console.log(
          '[PinCodeService] 📱 phoneVerified:',
          savedUser?.phoneVerified
        );

        console.log(
          '[PinCodeService] 👤 Saved user:',
          savedUser
        );

        console.log(
          '[PinCodeService] ========================================'
        );

        // Add the updated user to the returned response
        // so PinCodePage and Redux can use the same data.
        if (
          responseData?.data &&
          typeof responseData.data ===
            'object'
        ) {
          responseData.data.user =
            savedUser;
        } else {
          responseData.user =
            savedUser;
        }
      }

      return responseData;

    } catch (error) {
      console.error(
        '[PinCodeService] ❌ PIN verification failed:',
        error
      );

      throw this.handleError(error);
    }
  }

  // ==========================================================
  // PIN STATUS CHECKS
  // ==========================================================

  /**
   * Check if PIN is valid and not expired
   * GET /api/pin/is-valid
   */
  async isValidPin() {
    try {
      const response =
        await api.get(
          '/pin/is-valid'
        );

      return response.data;

    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Check if PIN is verified
   * GET /api/pin/is-verified
   */
  async isPinVerified() {
    try {
      const response =
        await api.get(
          '/pin/is-verified'
        );

      return response.data;

    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get PIN status
   * GET /api/pin/status
   */
  async getPinStatus() {
    try {
      const response =
        await api.get(
          '/pin/status'
        );

      return response.data;

    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==========================================================
  // PIN MANAGEMENT
  // ==========================================================

  /**
   * Reset PIN
   * POST /api/pin/reset
   */
  async resetPin() {
    try {
      const response =
        await api.post(
          '/pin/reset'
        );

      return response.data;

    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Regenerate PIN
   * POST /api/pin/regenerate
   */
  async regeneratePin() {
    try {
      const response =
        await api.post(
          '/pin/regenerate'
        );

      return response.data;

    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==========================================================
  // UTILITY
  // ==========================================================

  /**
   * Mask phone number for logging
   * POST /api/pin/mask-phone
   */
  async maskPhoneNumber(
    phone
  ) {
    try {
      const response =
        await api.post(
          '/pin/mask-phone',
          {
            phone
          }
        );

      return response.data;

    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==========================================================
  // HELPER METHODS
  // ==========================================================

  /**
   * Handle API errors
   */
  handleError(
    error
  ) {
    let errorMessage =
      ' ';

    if (
      error.response?.data?.data?.error?.message
    ) {
      errorMessage =
        error.response.data.data.error.message;

    } else if (
      error.response?.data?.error?.message
    ) {
      errorMessage =
        error.response.data.error.message;

    } else if (
      error.response?.data?.message
    ) {
      errorMessage =
        error.response.data.message;

    } else if (
      error.message
    ) {
      errorMessage =
        error.message;
    }

    return {
      success: false,
      error: errorMessage,
      statusCode:
        error.response?.status,
      data:
        error.response?.data
    };
  }

  // ==========================================================
  // CLIENT-SIDE PIN GENERATOR
  // ==========================================================

  generatePinClientSide() {
    return Math.floor(
      100000 +
      Math.random() * 900000
    ).toString();
  }

  // ==========================================================
  // MASK PHONE CLIENT-SIDE
  // ==========================================================

  maskPhoneNumberClientSide(
    phone
  ) {
    if (!phone) {
      return 'unknown';
    }

    const visible =
      phone.length > 4
        ? phone.slice(-4)
        : phone;

    return `****${visible}`;
  }

  // ==========================================================
  // VALIDATE PIN
  // ==========================================================

  validatePinFormat(
    pin
  ) {
    if (!pin) {
      return {
        valid: false,
        error: 'PIN is required'
      };
    }

    if (
      typeof pin !==
      'string'
    ) {
      return {
        valid: false,
        error: 'PIN must be a string'
      };
    }

    if (
      pin.length !== 6
    ) {
      return {
        valid: false,
        error:
          'PIN must be exactly 6 digits'
      };
    }

    if (
      !/^\d{6}$/.test(
        pin
      )
    ) {
      return {
        valid: false,
        error:
          'PIN must contain only digits'
      };
    }

    return {
      valid: true
    };
  }

  // ==========================================================
  // DEFAULT EXPIRY
  // ==========================================================

  getDefaultExpiryMinutes() {
    return 15;
  }

  // ==========================================================
  // MAX ATTEMPTS
  // ==========================================================

  getMaxAttempts() {
    return 5;
  }

  // ==========================================================
  // LOCK DURATION
  // ==========================================================

  getLockDurationMinutes() {
    return 30;
  }

  // ==========================================================
  // FORMAT PIN STATUS
  // ==========================================================

  formatPinStatus(
    status
  ) {
    if (!status) {
      return null;
    }

    return {
      hasPin:
        status.exists ||
        false,

      isVerified:
        status.verified ||
        false,

      isExpired:
        status.expired ||
        false,

      isLocked:
        status.locked ||
        false,

      attemptsUsed:
        status.attemptsUsed ||
        0,

      attemptsRemaining:
        status.attemptsRemaining ||
        0,

      expiresAt:
        status.expiresAt
          ? new Date(
              status.expiresAt
            )
          : null,

      lockedUntil:
        status.lockedUntil
          ? new Date(
              status.lockedUntil
            )
          : null,

      sentToEmail:
        status.sentToEmail ||
        false,

      sentToPhone:
        status.sentToPhone ||
        false,

      emailVerified:
        status.emailVerified ||
        false,

      phoneVerified:
        status.phoneVerified ||
        false,

      isActive:
        status.exists &&
        !status.expired &&
        !status.locked &&
        !status.verified,

      canAttempt:
        status.exists &&
        !status.expired &&
        !status.locked &&
        !status.verified &&
        status.attemptsRemaining > 0
    };
  }

  // ==========================================================
  // STATUS MESSAGE
  // ==========================================================

  getStatusMessage(
    status
  ) {
    if (!status) {
      return 'No PIN status available';
    }

    if (
      status.verified
    ) {
      return '✅ PIN already verified';
    }

    if (
      status.locked
    ) {
      return '🔒 PIN is locked. Please try again later.';
    }

    if (
      status.expired
    ) {
      return '⏰ PIN has expired. Please request a new one.';
    }

    if (
      !status.exists
    ) {
      return '📱 No PIN found. Please request a new PIN.';
    }

    if (
      status.attemptsRemaining === 0
    ) {
      return '❌ Too many failed attempts. PIN is locked.';
    }

    if (
      status.attemptsRemaining > 0
    ) {
      return `📝 Enter your PIN (${status.attemptsRemaining} attempts remaining)`;
    }

    return '📱 Please enter your PIN';
  }
}

// ============================================================
// EXPORT SINGLETON
// ============================================================

export default new PinCodeService();